/**
 * useSprayVersion — the holds of one wall version, read once, kept in memory.
 *
 * A single `getDoc` per mount / `versionId` change, never a listener: the holds
 * of a live version change only through the admin's hold edits, and the problem
 * editor calls `reload()` when the server answers `versionNotLive`. `holds` is
 * the decoded list (`points` added, holds without an outline dropped).
 *
 * The kept copy (docs/spec/21g-spraywall-problems-ui.md, "The kept copy"): the
 * last read version of each `gymId|wallId|versionId` stays in a module-level
 * store (12 at most, least recently used dropped). A mount whose version is
 * kept answers it at once and still reads in the background (stale-while-
 * revalidate), so the problem thumbnails draw their hold outlines on the first
 * frame instead of after the read. WHY it exists: the wait for this one read
 * was a dark placeholder on every wall open. `fresh: true` ignores the kept
 * copy for that mount (the problem editor writes against the holds and must
 * not start from a copy that may predate a hold edit). `prefetchSprayVersion`
 * fills the store ahead of the first open. Memory only: an app restart starts
 * empty, like every kept value of the app.
 *
 * One store, every mount follows it: mounts subscribe with
 * `useSyncExternalStore`, so an answer another mount or the prefetch brought in
 * reaches them at once. Every read is numbered when it STARTS and an answer is
 * applied only if no later-started read was applied before it, so a slow early
 * read can never overwrite a newer answer. An answer that changes nothing
 * notifies nobody.
 */
import { useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { decodeHolds } from "../utils/sprayWallEditorModel";

const EMPTY = Object.freeze([]);

/** Versions kept at once: a gym has a handful of walls, each version is a few KB of outlines. */
const MAX_KEPT = 12;

/** Keys whose last applied read number is remembered (an evicted key must still refuse an older answer). */
const MAX_SEQ_KEYS = 64;

/** key -> the last read version (Map order = recency, oldest first). */
const kept = new Map();
/** key -> the read in flight that a mount or a prefetch may join instead of reading twice. */
const inFlight = new Map();
/** key -> the number of the latest-started read whose answer was applied. */
const appliedSeq = new Map();
const listeners = new Set();
let readSeq = 0;

const sprayVersionKey = (gymId, wallId, versionId) => `${gymId}|${wallId}|${versionId}`;

function notify() {
  for (const listener of Array.from(listeners)) listener();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Test seam: forget every kept version, read number and shared in-flight read. */
export function resetSprayVersionStore() {
  kept.clear();
  inFlight.clear();
  appliedSeq.clear();
  notify();
}

/** Same content? Keeps the old object when a read answers the same, so nothing re-renders. */
function sameVersion(a, b) {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}

function touchKept(key) {
  if (!kept.has(key)) return;
  const value = kept.get(key);
  kept.delete(key);
  kept.set(key, value);
}

/**
 * Apply a read's answer to the store unless a read that started later was applied
 * already. Returns what the store holds for the key afterwards (the version, or
 * null). Notifies only when the kept set actually changed.
 */
function applyAnswer(key, seq, version) {
  if ((appliedSeq.get(key) || 0) > seq) return kept.get(key) || null;
  appliedSeq.delete(key);
  appliedSeq.set(key, seq);
  while (appliedSeq.size > MAX_SEQ_KEYS) appliedSeq.delete(appliedSeq.keys().next().value);

  const existing = kept.get(key);
  if (!version) {
    if (existing) { kept.delete(key); notify(); }
    return null;
  }
  if (existing && sameVersion(existing, version)) { touchKept(key); return existing; }
  kept.delete(key);
  kept.set(key, version);
  while (kept.size > MAX_KEPT) kept.delete(kept.keys().next().value);
  notify();
  return version;
}

/**
 * One `getDoc` of the version; its answer lands in the kept store whoever asks.
 * `join` shares the read with a concurrent one for the same version (a mount
 * during a prefetch); a forced read (`fresh`, `reload`) always goes to the server.
 */
function readVersion({ gymId, wallId, versionId }, join) {
  const key = sprayVersionKey(gymId, wallId, versionId);
  if (join) {
    const pending = inFlight.get(key);
    if (pending) return pending;
  }
  readSeq += 1;
  const seq = readSeq;
  const read = getDoc(doc(db, "gyms", gymId, "sprayWalls", wallId, "versions", versionId)).then((snap) => (
    applyAnswer(key, seq, snap.exists() ? { id: snap.id, ...snap.data() } : null)
  ));
  if (join) {
    inFlight.set(key, read);
    const clear = () => { if (inFlight.get(key) === read) inFlight.delete(key); };
    read.then(clear, clear);
  }
  return read;
}

/**
 * Fill the kept store for a version before anything opens it: one `getDoc`
 * unless the version is kept or already being read. Never rejects (a prefetch
 * is a convenience; the mount that needs the version reads it itself).
 */
export async function prefetchSprayVersion({ gymId, wallId, versionId } = {}) {
  try {
    if (!gymId || !wallId || !versionId) return;
    if (kept.has(sprayVersionKey(gymId, wallId, versionId))) return;
    await readVersion({ gymId, wallId, versionId }, true);
  } catch { /* a prefetch is a convenience */ }
}

export function useSprayVersion({ gymId, wallId, versionId, enabled, fresh = false }) {
  const active = Boolean(enabled && gymId && wallId && versionId);
  const scope = active ? sprayVersionKey(gymId, wallId, versionId) : "";
  // This mount's own read: its answer or error, and whether it has landed.
  const [own, setOwn] = useState(() => ({ scope, version: null, loaded: false, error: null }));
  const [reloadNonce, setReloadNonce] = useState(0);
  const forceRef = useRef(false);
  const showedKeptRef = useRef("");
  // The kept copy of this key; the snapshot is the stored object itself, so a mount
  // re-renders only when ITS key's copy changes.
  const getSnapshot = () => (scope ? kept.get(scope) : undefined);
  const keptNow = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    setOwn((s) => (s.scope === scope ? s : { scope, version: null, loaded: false, error: null }));
    if (!scope) return undefined;
    if (!fresh) touchKept(scope);
    const force = fresh || forceRef.current;
    forceRef.current = false;
    let cancelled = false;
    // A non-fresh mount that has a kept copy needs nothing from its own read but the
    // store update; a failed background read leaves the copy standing with no error.
    const land = (next) => setOwn((s) => (
      s.scope === next.scope && s.version === next.version && s.loaded === next.loaded && s.error === next.error ? s : next
    ));
    readVersion({ gymId, wallId, versionId }, !force).then((version) => {
      if (cancelled || (!fresh && kept.has(scope))) return;
      land({ scope, version, loaded: true, error: null });
    }, (error) => {
      if (cancelled || (!fresh && kept.has(scope))) return;
      land({ scope, version: null, loaded: true, error });
    });
    return () => { cancelled = true; };
  }, [scope, gymId, wallId, versionId, fresh, reloadNonce]);

  // A kept copy this mount showed was dropped (evicted, or the document is gone for
  // another mount) while it is open, and its own read did not settle it: read again
  // instead of staying "not loaded".
  useEffect(() => {
    if (fresh || !scope) return;
    if (keptNow) { showedKeptRef.current = scope; return; }
    if (showedKeptRef.current !== scope) return;
    showedKeptRef.current = "";
    if (own.scope === scope && own.loaded && !own.error) return;
    setReloadNonce((n) => n + 1);
  }, [keptNow, scope, fresh, own]);

  const reload = useCallback(() => {
    forceRef.current = true;
    setReloadNonce((n) => n + 1);
  }, []);

  const current = scope !== "" && own.scope === scope;
  const ownLanded = current && own.loaded;
  let version = null;
  let loaded = false;
  let error = null;
  if (scope !== "") {
    if (!fresh && keptNow) {
      version = keptNow;
      loaded = true;
    } else if (!fresh) {
      version = current ? own.version : null;
      loaded = ownLanded;
      error = current ? own.error : null;
    } else if (ownLanded) {
      // A fresh mount shows nothing until its own read has landed, then follows the store.
      error = own.error;
      version = error ? null : keptNow || own.version;
      loaded = true;
    }
  }
  const holds = useMemo(() => (version ? decodeHolds(version.holds) : EMPTY), [version]);
  return { version, holds, loaded, error, reload };
}
